import {
    ArrowsClockwiseIcon,
    TrashIcon,
    DownloadSimpleIcon,
    NotePencilIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { ActionBar, toast } from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { usePermissions } from '@/hooks';
import { Permission } from '@/types/permissions';
import { FullSource, SourceType } from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { DeleteSourceConfirmationModal } from '@/features/Source/components/DeleteSourceConfirmationModal';
import { EditSourceModal } from '@/features/Source/components/EditSourceModal';
import { ReanalyzeSourceConfirmationModal } from '@/features/Source/components/ReanalyzeSourceConfirmationModal';
import { useSourceReanalysis } from '@/features/Source/hooks/useSourceReanalysis';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { cleanupAfterSourceDeletion } from '@/stores';
import { revalidateIdentityViews } from '@/utils/swr';
import {
    revalidateAlertUnitCapacity,
    revalidateOrganizationConfiguration,
} from '@/features/SettingsView/hooks/useConfiguration';
import { useSourcesStore } from '@/stores/sources.store';
import { EngineService } from '@/services/engine';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS, getSourceCacheKey } from '@/constants/cache-keys';
import { isHdmiViewport } from '@/utils/source';
import { isAnalyzingSourceStatus } from '../sourceStatusDisplay';
import { revalidateSourceList } from '@/hooks/useSourceListInfinite';

interface SourceActionBarProps {
    source: FullSource;
}

export function SourceActionBar({ source }: SourceActionBarProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { canPerform } = usePermissions();
    const navigate = useNavigate();
    const { mutate } = useSWRConfig();
    const updateSource = useSourcesStore((state) => state.updateSource);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [reanalyzeConfirm, setReanalyzeConfirm] = useState(false);
    const { reanalyzeSource, isStartingAnalysis } = useSourceReanalysis();

    const { id: sourceId } = source;
    const viewport = isHdmiViewport(sourceId);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = EngineService.getSourceDownloadUrl(sourceId);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDeleteClick = () => {
        setDeleteConfirm(true);
    };

    const handleEditClick = () => {
        setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
    };

    const handleReanalyzeConfirm = async () => {
        await reanalyzeSource(source);
        setReanalyzeConfirm(false);
    };

    const handleEditSubmit = async (name: string) => {
        try {
            await EngineService.updateSource(sourceId, { name });
            updateSource(sourceId, { name });
            await mutate(getSourceCacheKey(sourceId));
            await revalidateSourceList();
            await mutate(
                (key) =>
                    Array.isArray(key) &&
                    key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN,
            );
            toast.success(t('toast.editSuccess'));
        } catch (error) {
            toast.error(t('toast.editError'));
            throw error;
        }
    };

    const handleDeleteConfirm = async () => {
        if (isDeleting) return;

        setIsDeleting(true);
        try {
            await EngineService.deleteSource(sourceId);
            await revalidateIdentityViews();
            await revalidateOrganizationConfiguration();
            await revalidateAlertUnitCapacity();
            toast.success(t('toast.deleteSuccess'));
            navigate(AppRoutes.SOURCES, { replace: true });

            setTimeout(() => {
                cleanupAfterSourceDeletion(sourceId);
            }, 0);
        } catch (error) {
            toast.error(t('toast.deleteError'));
        } finally {
            setIsDeleting(false);
            setDeleteConfirm(false);
        }
    };

    const actions: ActionConfig[] = [];

    if (canPerform(Permission.EDIT, source.isOwner)) {
        actions.push({
            type: ActionType.ITEM,
            icon: NotePencilIcon,
            label: t('actions.edit'),
            onClick: handleEditClick,
            permission: Permission.EDIT,
        });
    }

    if (source.type === SourceType.FILE) {
        actions.push({
            type: ActionType.ITEM,
            icon: DownloadSimpleIcon,
            label: t('actions.download'),
            onClick: handleDownload,
            permission: Permission.DOWNLOAD,
        });
    }

    if (
        source.type === SourceType.FILE &&
        canPerform(Permission.EDIT, source.isOwner)
    ) {
        actions.push({
            type: ActionType.ITEM,
            icon: ArrowsClockwiseIcon,
            label: isStartingAnalysis
                ? t('actions.startingReanalysis')
                : t('actions.reanalyze'),
            onClick: () => setReanalyzeConfirm(true),
            permission: Permission.EDIT,
            disabled:
                isStartingAnalysis || isAnalyzingSourceStatus(source.status),
        });
    }

    if (canPerform(Permission.DELETE, source.isOwner)) {
        actions.push({
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: isDeleting ? t('actions.deleting') : t('actions.delete'),
            onClick: () => handleDeleteClick(),
            permission: Permission.DELETE,
            disabled: isDeleting || viewport,
        });
    }

    return (
        <>
            <ActionBar actions={actions} />
            <DeleteSourceConfirmationModal
                isOpen={deleteConfirm}
                deleteInProgress={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteConfirm(false)}
            />
            <EditSourceModal
                isOpen={isEditModalOpen}
                onClose={handleEditModalClose}
                initialName={source.name}
                lockExtension={source.type === SourceType.FILE}
                onSubmit={handleEditSubmit}
            />
            <ReanalyzeSourceConfirmationModal
                isOpen={reanalyzeConfirm}
                onCancel={() => setReanalyzeConfirm(false)}
                onConfirm={() => void handleReanalyzeConfirm()}
                submitInProgress={isStartingAnalysis}
            />
        </>
    );
}
